import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, Star, Zap, Crown, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import GlassCard from "@/components/GlassCard";
import NeonButton from "@/components/NeonButton";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const freeFeatures = [
  "Basic frequency generator (1 oscillator)",
  "3 binaural beat presets",
  "White noise only",
  "5 minute sessions max",
  "No cloud sync"
];

const proFeatures = [
  "4-oscillator frequency generator (0.1Hz - 20kHz)",
  "All 6 binaural presets + custom",
  "All background sounds (White, Pink, Brown, Blue)",
  "All 8 audio effects (Chorus, Reverb, Spatial, etc.)",
  "6 guided breathwork exercises",
  "Sleep Mode with auto-transitions",
  "Session timer up to 2 hours",
  "Export audio (WAV/MP3) up to 59 min",
  "Unlimited cloud presets sync",
  "AI Subliminal creation",
  "All templates library access",
  "Priority support"
];

const Pricing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly"); // "monthly" or "annual"

  // Check for returning from Stripe
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId && isAuthenticated) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams, isAuthenticated]);

  // Load subscription status
  useEffect(() => {
    if (isAuthenticated) {
      loadSubscriptionStatus();
    }
  }, [isAuthenticated]);

  const loadSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`);
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error("Failed to load subscription status:", error);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    try {
      const response = await axios.get(`${API}/subscription/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === "paid") {
        toast.success("Payment successful! Welcome to Subspace Pro!");
        setSubscriptionStatus({ is_premium: true });
        // Clear URL params
        navigate("/pricing", { replace: true });
      } else {
        toast.error("Payment not completed. Please try again.");
      }
    } catch (error) {
      console.error("Payment check failed:", error);
      toast.error("Failed to verify payment");
    } finally {
      setCheckingPayment(false);
    }
  };

  const handleSubscribe = async () => {
    if (!isAuthenticated) {
      toast.error("Please sign in first to subscribe");
      return;
    }

    if (subscriptionStatus?.is_premium) {
      toast.info("You already have an active subscription!");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/subscription/checkout`, {
        origin_url: window.location.origin,
        plan: billingCycle
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error("Checkout failed:", error);
      toast.error(error.response?.data?.detail || "Failed to start checkout");
    } finally {
      setLoading(false);
    }
  };

  const isPremium = subscriptionStatus?.is_premium || user?.is_admin;

  const monthlyPrice = 9.99;
  const annualPrice = 59.99;
  const annualMonthly = (annualPrice / 12).toFixed(2);
  const savings = Math.round((1 - annualPrice / (monthlyPrice * 12)) * 100);

  return (
    <div className="min-h-screen pt-20 md:pt-8 pb-32 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <p className="tagline-shimmer text-xs font-semibold uppercase mb-3" data-testid="pricing-tagline">
            Expand Your Mind.
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
            Unlock Your <span className="text-neon">Full Potential</span>
          </h1>
          <p className="text-gray-400 max-w-xl mx-auto">
            Get unlimited access to all sound therapy tools, effects, and features
          </p>
        </motion.div>

        {/* Billing Toggle */}
        {!isPremium && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex justify-center mb-8"
          >
            <div className="flex items-center gap-4 p-1 bg-white/5 rounded-full border border-white/10">
              <button
                onClick={() => setBillingCycle("monthly")}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  billingCycle === "monthly"
                    ? "bg-violet-500 text-white"
                    : "text-gray-400 hover:text-white"
                }`}
                data-testid="billing-monthly"
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle("annual")}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                  billingCycle === "annual"
                    ? "bg-violet-500 text-white"
                    : "text-gray-400 hover:text-white"
                }`}
                data-testid="billing-annual"
              >
                Annual
                <span className="px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-400">
                  Save {savings}%
                </span>
              </button>
            </div>
          </motion.div>
        )}

        {checkingPayment && (
          <div className="text-center py-12">
            <Loader2 className="animate-spin mx-auto mb-4 text-violet-500" size={48} />
            <p className="text-white">Verifying your payment...</p>
          </div>
        )}

        {!checkingPayment && (
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Free Plan */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              <GlassCard hover={false} className="h-full">
                <div className="flex items-center gap-2 mb-4">
                  <Zap size={24} className="text-gray-400" />
                  <h2 className="text-xl font-bold text-white">Free</h2>
                </div>
                
                <div className="mb-6">
                  <span className="text-4xl font-bold text-white">€0</span>
                  <span className="text-gray-500">/month</span>
                </div>

                <p className="text-sm text-gray-400 mb-6">
                  Get started with basic sound therapy
                </p>

                <ul className="space-y-3 mb-8">
                  {freeFeatures.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-400">
                      <Check size={16} className="text-gray-500 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <NeonButton 
                  variant="secondary" 
                  className="w-full"
                  onClick={() => navigate("/studio")}
                  data-testid="try-free-btn"
                >
                  Try Free
                </NeonButton>
              </GlassCard>
            </motion.div>

            {/* Pro Plan */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <GlassCard hover={false} className="h-full border-violet-500/50 relative overflow-hidden">
                {/* Popular badge */}
                <div className="absolute top-4 right-4">
                  <span className="px-3 py-1 text-xs font-medium rounded-full bg-violet-500 text-white">
                    {billingCycle === "annual" ? "BEST VALUE" : "MOST POPULAR"}
                  </span>
                </div>

                <div className="flex items-center gap-2 mb-4">
                  <Crown size={24} className="text-violet-400" />
                  <h2 className="text-xl font-bold text-white">Pro</h2>
                </div>
                
                <div className="mb-2">
                  {billingCycle === "annual" ? (
                    <>
                      <span className="text-4xl font-bold text-white">€{annualMonthly}</span>
                      <span className="text-gray-500">/month</span>
                    </>
                  ) : (
                    <>
                      <span className="text-4xl font-bold text-white">€{monthlyPrice}</span>
                      <span className="text-gray-500">/month</span>
                    </>
                  )}
                </div>
                
                {billingCycle === "annual" && (
                  <div className="mb-4">
                    <span className="text-sm text-gray-400">
                      Billed €{annualPrice}/year
                    </span>
                    <span className="ml-2 text-sm text-green-400">
                      (Save €{((monthlyPrice * 12) - annualPrice).toFixed(2)})
                    </span>
                  </div>
                )}
                
                {billingCycle === "monthly" && (
                  <div className="mb-4 h-5"></div>
                )}

                <p className="text-sm text-gray-400 mb-6">
                  Unlock all features for complete sound mastery
                </p>

                <ul className="space-y-3 mb-8 max-h-72 overflow-y-auto">
                  {proFeatures.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                      <Check size={16} className="text-violet-400 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {isPremium ? (
                  <div className="text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/20 text-green-400 mb-2">
                      <Check size={16} />
                      <span className="text-sm font-medium">Active Subscription</span>
                    </div>
                    <p className="text-xs text-gray-500">
                      {user?.is_admin ? "Admin access - unlimited features" : "Thank you for being a Pro member!"}
                    </p>
                  </div>
                ) : (
                  <NeonButton 
                    className="w-full"
                    onClick={handleSubscribe}
                    disabled={loading}
                    data-testid="subscribe-btn"
                  >
                    {loading ? (
                      <Loader2 className="animate-spin" size={18} />
                    ) : (
                      <>
                        {billingCycle === "annual" ? `Subscribe for €${annualPrice}/year` : `Subscribe for €${monthlyPrice}/month`}
                        <ArrowRight size={18} className="ml-2" />
                      </>
                    )}
                  </NeonButton>
                )}
              </GlassCard>
            </motion.div>
          </div>
        )}

        {/* Payment Methods */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-center mt-12"
        >
          <p className="text-xs text-gray-500 mb-4">Secure payments powered by</p>
          <div className="flex items-center justify-center gap-6">
            <div className="flex items-center gap-2 text-gray-400">
              <svg className="h-8" viewBox="0 0 60 25" fill="currentColor">
                <path d="M59.64 14.28h-8.06v-2.9h8.06v2.9zm0-5.72h-8.06V5.66h8.06v2.9zM10.75 6.59c-.53-.32-1.2-.48-2-.48H5.31v5.53h3.44c.8 0 1.47-.16 2-.48.87-.53 1.3-1.4 1.3-2.28 0-.89-.43-1.76-1.3-2.29zm.48 7.43c-.67-.4-1.48-.6-2.43-.6H5.31v6.3h3.49c.95 0 1.76-.2 2.43-.6.95-.57 1.43-1.52 1.43-2.55 0-1.03-.48-1.98-1.43-2.55zM0 2.04h9.47c2.58 0 4.5.74 5.76 2.21.89 1.04 1.33 2.23 1.33 3.58 0 1.71-.66 3.06-1.98 4.05.89.55 1.57 1.18 2.04 1.9.66 1.02 1 2.23 1 3.61 0 1.86-.68 3.42-2.04 4.68-1.36 1.26-3.15 1.89-5.37 1.89H0V2.04zm25.13 0v17.92h-5.06V2.04h5.06zm26.8 8.96c0-5.72-3.68-8.96-8.8-8.96h-7.87v17.92h7.87c5.12 0 8.8-3.24 8.8-8.96zm-5.29 0c0 3.05-1.56 5.08-4.31 5.08h-2.01V5.92h2.01c2.75 0 4.31 2.03 4.31 5.08z"/>
              </svg>
              <span className="text-sm">Stripe</span>
            </div>
            <div className="text-gray-600">|</div>
            <div className="flex items-center gap-1 text-gray-400">
              <svg className="h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 24c6.627 0 12-5.373 12-12S18.627 0 12 0 0 5.373 0 12s5.373 12 12 12z"/>
                <path fill="#fff" d="M5.5 12.5h3v6h-3zm4-4h3v10h-3zm4 2h3v8h-3z"/>
              </svg>
              <span className="text-sm">Google Pay</span>
            </div>
            <div className="text-gray-600">|</div>
            <div className="flex items-center gap-1 text-gray-400">
              <svg className="h-5" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="12" r="12"/>
                <path fill="#fff" d="M7 8h10v2H7zm0 3h10v2H7zm0 3h7v2H7z"/>
              </svg>
              <span className="text-sm">Card</span>
            </div>
          </div>
        </motion.div>

        {/* FAQ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-16 max-w-2xl mx-auto"
        >
          <h3 className="text-xl font-bold text-white mb-6 text-center">
            Frequently Asked Questions
          </h3>
          
          <div className="space-y-4">
            {[
              {
                q: "Can I cancel anytime?",
                a: "Yes, you can cancel your subscription at any time. You'll continue to have Pro access until the end of your billing period."
              },
              {
                q: "What payment methods do you accept?",
                a: "We accept all major credit/debit cards, Google Pay, and Apple Pay through our secure Stripe payment system."
              },
              {
                q: "Is there a free trial?",
                a: "The Free plan lets you try basic features. Upgrade to Pro when you're ready for the full experience."
              },
              {
                q: "What's the difference between monthly and annual?",
                a: `Annual billing saves you €${((monthlyPrice * 12) - annualPrice).toFixed(2)} per year (${savings}% off). You're billed once per year instead of monthly.`
              },
              {
                q: "What happens to my data if I cancel?",
                a: "Your cloud presets and creations remain accessible. You can export them anytime before downgrading."
              }
            ].map((faq, i) => (
              <GlassCard key={i} hover={false} className="p-4">
                <h4 className="text-sm font-medium text-white mb-2">{faq.q}</h4>
                <p className="text-xs text-gray-400">{faq.a}</p>
              </GlassCard>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Pricing;
